import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "types";
import {
  mockQuoteAccessories,
  mockQuoteBoxStructure,
  mockQuoteDetails,
  mockQuoteForm,
} from "../data";
import { QuoteStatus } from "../types";
import { initialState } from "./initialState";

const selectDomain = (state: RootState) => state.quotesState || initialState;

export const selectQuotesSearch = createSelector(
  [selectDomain],
  (quotesState) => quotesState.search
);

export const selectQuotesStatusFilter = createSelector(
  [selectDomain],
  (quotesState) => quotesState.statusFilter
);

export const selectQuotesLoading = createSelector(
  [selectDomain],
  (quotesState) => quotesState.loading
);

export const selectQuoteDetailsLoading = createSelector(
  [selectDomain],
  (quotesState) => quotesState.detailLoading
);

export const selectSelectedQuote = createSelector(
  [selectDomain],
  (quotesState) => quotesState.selectedQuote
);

export const selectQuoteDetails = createSelector(
  [selectDomain],
  (quotesState) => quotesState.quoteDetails ?? mockQuoteDetails
);

export const selectQuoteQuotationForm = createSelector(
  [selectDomain],
  (quotesState) => quotesState.quotationForm ?? mockQuoteForm
);

export const selectQuoteBoxStructure = createSelector(
  [selectDomain],
  (quotesState) => quotesState.boxStructure ?? mockQuoteBoxStructure
);

export const selectQuoteAccessories = createSelector(
  [selectDomain],
  (quotesState) => quotesState.accessories ?? mockQuoteAccessories
);

export const selectQuoteTyingEquipment = createSelector(
  [selectQuoteAccessories],
  (accessories) => accessories.tyingEquipment
);

export const selectQuoteStorageAccessories = createSelector(
  [selectQuoteAccessories],
  (accessories) => accessories.storageAccessories
);

export const selectQuoteAccessoryTables = createSelector(
  [selectQuoteAccessories],
  (accessories) => accessories.tables
);

export const selectQuoteSafetyAndStandards = createSelector(
  [selectQuoteAccessories],
  (accessories) => accessories.safetyAndStandards
);

export const selectQuoteVehicleAddons = createSelector(
  [selectQuoteAccessories],
  (accessories) => accessories.vehicleAddons
);

export const selectQuoteLocks = createSelector(
  [selectQuoteAccessories],
  (accessories) => accessories.locks
);

export const selectQuoteStandard = createSelector(
  [selectQuoteAccessories],
  (accessories) => accessories.standard
);

export const selectQuoteById = (quoteId: string) =>
  createSelector([selectDomain], (quotesState) => {
    if (quotesState.selectedQuote?.id === quoteId) {
      return quotesState.selectedQuote;
    }

    return quotesState.list.find((quote) => quote.id === quoteId) || null;
  });

export const selectQuotesList = createSelector(
  [selectDomain],
  (quotesState) => {
    const { list, search, statusFilter } = quotesState;
    const searchValue = search.trim().toLowerCase();

    return list.filter((quote) => {
      const matchesSearch =
        !searchValue ||
        quote.quoteNumber.toLowerCase().includes(searchValue) ||
        quote.customerNameKey.toLowerCase().includes(searchValue) ||
        quote.projectTypeKey.toLowerCase().includes(searchValue);

      const matchesStatus =
        statusFilter.includes("ALL") ||
        statusFilter.includes(quote.status);

      return matchesSearch && matchesStatus;
    });
  }
);

export const selectQuoteStatusLabelKey = (status: QuoteStatus) => {
  switch (status) {
    case QuoteStatus.DRAFT:
      return "QUOTES.STATUS.DRAFT";

    case QuoteStatus.SENT:
      return "QUOTES.STATUS.SENT";

    case QuoteStatus.ACCEPTED:
      return "QUOTES.STATUS.ACCEPTED";

    default:
      return "";
  }
};
